import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Stack,
  Button,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import { Document, Page, pdfjs } from "react-pdf";
import workerSrc from "pdfjs-dist/build/pdf.worker.min.mjs?url";

// Configure pdf.js worker for Vite
pdfjs.GlobalWorkerOptions.workerSrc = workerSrc;

const MIN_SCALE = 0.75;
const MAX_SCALE = 2;
const SCALE_STEP = 0.25;

const PdfModal = ({ open, onClose, title = "", fileUrl }) => {
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [scale, setScale] = useState(1);

  useEffect(() => {
    if (open) {
      setNumPages(null);
      setPageNumber(1);
      setScale(1);
    }
  }, [open, fileUrl]);

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
  };

  const handlePrev = () => setPageNumber((p) => Math.max(1, p - 1));
  const handleNext = () => setPageNumber((p) => Math.min(numPages || 1, p + 1));
  const handleZoomIn = () => setScale((s) => Math.min(MAX_SCALE, s + SCALE_STEP));
  const handleZoomOut = () => setScale((s) => Math.max(MIN_SCALE, s - SCALE_STEP));

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ pr: 6 }}>
        {title}
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {/* Controls */}
        <Stack direction="row" alignItems="center" spacing={1} mb={2}>
          <Button
            variant="outlined"
            onClick={handlePrev}
            disabled={!numPages || pageNumber <= 1}
            startIcon={<NavigateBeforeIcon />}
          >
            Prev
          </Button>
          <Button
            variant="outlined"
            onClick={handleNext}
            disabled={!numPages || pageNumber >= (numPages || 1)}
            endIcon={<NavigateNextIcon />}
          >
            Next
          </Button>
          <Button
            variant="outlined"
            onClick={handleZoomOut}
            disabled={scale <= MIN_SCALE}
            startIcon={<ZoomOutIcon />}
          >
            Zoom Out
          </Button>
          <Button
            variant="outlined"
            onClick={handleZoomIn}
            disabled={scale >= MAX_SCALE}
            startIcon={<ZoomInIcon />}
          >
            Zoom In
          </Button>
          <Typography variant="body2" sx={{ ml: "auto" }}>
            Page {numPages ? pageNumber : 0} of {numPages || 0}
          </Typography>
        </Stack>

        {/* Viewer */}
        {!fileUrl ? (
          <Typography variant="body2" color="text.secondary">
            Document not available. Please add the PDF under src/assets/docs and try again.
          </Typography>
        ) : (
          <div style={{ width: "100%", display: "flex", justifyContent: "center" }}>
            <Document file={fileUrl} onLoadSuccess={onDocumentLoadSuccess} onLoadError={console.error}>
              <Page
                pageNumber={pageNumber}
                scale={scale}
                renderTextLayer={false}
                renderAnnotationLayer={false}
              />
            </Document>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default PdfModal;
